import { useState, useRef, useEffect, type ReactNode } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { cn } from '@/utils/helpers';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { SignedInPill } from './SignedInPill';
import {
  House, Users, Palette, ToggleLeft, MapPin,
  Globe, CreditCard, Settings, ArrowLeft, Menu, Layers, Megaphone, ChevronDown, ChevronRight, ChevronLeft, LogOut, Newspaper, Handshake, Mail, Sun, Moon, Table2, Database, BarChart3, MessageSquare, BookOpen, ShoppingBag, Smartphone,
} from 'lucide-react';
import { PlatformAnnouncementBanner } from '@/components/shared/PlatformAnnouncementBanner';

// ── Nav model ─────────────────────────────────────────────────────────
// A nav entry is either a leaf (`path` set) or a group (`children` set).
// Groups render as an expandable parent — click the row to toggle, or
// the group auto-expands when one of its children is the active route.
type NavLeaf  = { label: string; path: string; icon: any };
type NavGroup = { label: string; icon: any; children: NavLeaf[] };
type NavItem  = NavLeaf | NavGroup;
const isGroup = (n: NavItem): n is NavGroup => 'children' in n;

const NAV: NavItem[] = [
  { label: 'Dashboard', path: '',          icon: House },
  { label: 'Branding',  path: '/branding', icon: Palette },

  // ── Plans (parent) ──
  { label: 'Plans', icon: Layers, children: [
    { label: 'Your Plan',           path: '/features',       icon: ToggleLeft },
    { label: 'Platform Plans',      path: '/platform-plans', icon: Table2 },
    { label: 'Your Customer Plans', path: '/b2c-plans',      icon: Layers },
  ]},

  { label: 'Users',       path: '/users',        icon: Users },
  { label: 'App Installs', path: '/app-installs', icon: Smartphone },
  { label: 'Trips',     path: '/trips',    icon: MapPin },
  // Stories — operator-side moderation surface for the public /feed.
  // Lists all journals (drafts + published + hidden) belonging to the
  // tenant, with per-row Hide / Unhide. Sits next to Trips because
  // both are "what your travelers are producing." See migration
  // 20260728_journal_tenant_moderation for the column shape that
  // backs the Hidden state.
  { label: 'Stories',   path: '/stories',  icon: BookOpen },
  { label: 'Announcements', path: '/announcements', icon: Megaphone },

  // ── Booking / Affiliates (parent) ──
  { label: 'Booking / Affiliates', icon: Handshake, children: [
    { label: 'Bulletin Board',         path: '/bulletins',             icon: Newspaper },
    { label: 'Affiliates',             path: '/affiliates',            icon: Handshake },
    { label: 'Gear Picks',             path: '/gear-picks',            icon: ShoppingBag },
    { label: 'Affiliate Performance',  path: '/affiliate-performance', icon: BarChart3 },
  ]},

  { label: 'Domain',    path: '/domain',   icon: Globe },
  { label: 'Billing',   path: '/billing',  icon: CreditCard },
  { label: 'Email',     path: '/email',    icon: Mail },
  { label: 'Data Retention', path: '/data-retention', icon: Database },
  { label: 'Feedback',  path: '/feedback', icon: MessageSquare },
  { label: 'Settings',  path: '/settings', icon: Settings },
];

export function TenantAdminLayout({ children, tenantName, brandColor }: {
  children: ReactNode; tenantName?: string; brandColor?: string;
}) {
  const loc = useLocation();
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { user, myTenants, isSuperAdmin, signOut } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Sidebar collapse — persisted across reloads, matches SuperAdminLayout.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('tenant-admin-sidebar-collapsed') === 'true'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('tenant-admin-sidebar-collapsed', String(collapsed)); } catch {}
  }, [collapsed]);
  const [tooltip, setTooltip] = useState<{ label: string; y: number } | null>(null);
  const showTooltip = (label: string, e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({ label, y: rect.top + rect.height / 2 });
  };
  const hideTooltip = () => setTooltip(null);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const base = `/admin/tenants/${tenantId}/manage`;

  const hasMultipleTenants = myTenants.length > 1 || isSuperAdmin;

  // Close switcher on outside click
  useEffect(() => {
    if (!switcherOpen) return;
    const handler = (e: MouseEvent) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) setSwitcherOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [switcherOpen]);

  const isActive = (path: string) => {
    const full = base + path;
    if (path === '') return loc.pathname === base || loc.pathname === base + '/';
    return loc.pathname.startsWith(full);
  };

  // Auto-expand any group that contains the active route, so the user
  // never lands on a route whose parent is collapsed.
  const initiallyOpenGroups = (): Set<string> => {
    const open = new Set<string>();
    for (const item of NAV) {
      if (isGroup(item) && item.children.some(c => isActive(c.path))) {
        open.add(item.label);
      }
    }
    return open;
  };
  const [openGroups, setOpenGroups] = useState<Set<string>>(initiallyOpenGroups);
  // Re-sync when route changes — guarantees parent expands on direct nav.
  useEffect(() => { setOpenGroups(initiallyOpenGroups()); /* eslint-disable-next-line */ }, [loc.pathname]);

  const toggleGroup = (label: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-surface-50">
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />}

      <aside className={cn(
        'fixed lg:relative z-50 h-full bg-surface-100 border-r border-overlay/[0.06] flex flex-col transition-all duration-300 lg:translate-x-0',
        collapsed ? 'w-[72px]' : 'w-[230px]',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
      )}>
        {/* Logo */}
        <div className={cn(
          'flex items-center h-14 border-b border-overlay/[0.06] shrink-0',
          collapsed ? 'justify-center px-2' : 'gap-3 px-4',
        )}>
          <img src="/logo.svg" alt="DiscoveryGem" className="w-7 h-7 shrink-0 dark:brightness-0 dark:invert" />
          {!collapsed && (
            <div>
              <div className="font-semibold text-surface-900 text-sm leading-tight">DiscoveryGem</div>
              <div className="text-[9px] text-surface-500 uppercase tracking-widest font-medium">Platform</div>
            </div>
          )}
        </div>

        <div className={cn('border-b border-overlay/[0.06]', collapsed ? 'p-2' : 'p-4')}>
          {isSuperAdmin && !collapsed && (
            <Link to="/admin/tenants" className="flex items-center gap-2 text-xs text-surface-500 hover:text-surface-800 transition-colors mb-3">
              <ArrowLeft size={12} /> All Tenants
            </Link>
          )}
          {isSuperAdmin && collapsed && (
            <Link
              to="/admin/tenants"
              onMouseEnter={(e) => showTooltip('All Tenants', e)}
              onMouseLeave={hideTooltip}
              className="flex items-center justify-center w-full p-1.5 mb-2 rounded-lg hover:bg-overlay/[0.04] text-surface-500 hover:text-surface-800 transition-colors"
            >
              <ArrowLeft size={14} />
            </Link>
          )}
          <div className="relative" ref={switcherRef}>
            <button
              onClick={() => hasMultipleTenants && setSwitcherOpen(!switcherOpen)}
              onMouseEnter={(e) => collapsed && showTooltip(tenantName || 'Tenant', e)}
              onMouseLeave={hideTooltip}
              className={cn(
                'flex items-center w-full text-left rounded-lg transition-colors',
                collapsed ? 'justify-center' : 'gap-2.5',
                hasMultipleTenants && (collapsed ? 'hover:bg-overlay/[0.04] p-1 cursor-pointer' : 'hover:bg-overlay/[0.04] -mx-1 px-1 py-1 cursor-pointer'),
              )}
            >
              <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-gradient-to-br from-slate-700 to-slate-900 shadow-sm ring-1 ring-slate-500/30">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
              </div>
              {!collapsed && (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-surface-900 truncate">{tenantName || 'Tenant'}</div>
                    <div className="text-[10px] text-surface-500 uppercase tracking-wider">Tenant Admin</div>
                  </div>
                  {hasMultipleTenants && <ChevronDown size={14} className={cn('text-surface-400 transition-transform', switcherOpen && 'rotate-180')} />}
                </>
              )}
            </button>

            {/* Switcher dropdown */}
            {switcherOpen && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-surface-0 border border-overlay/[0.06] rounded-xl shadow-elevated z-50 max-h-60 overflow-y-auto">
                {myTenants
                  .filter(({ tenant }) => tenant.id !== tenantId)
                  .map(({ tenant, role }) => (
                    <button
                      key={tenant.id}
                      onClick={() => {
                        setSwitcherOpen(false);
                        navigate(`/admin/tenants/${tenant.id}/manage`);
                      }}
                      className="flex items-center gap-2.5 w-full px-3 py-2.5 hover:bg-overlay/[0.04] transition-colors text-left"
                    >
                      <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 bg-gradient-to-br from-slate-700 to-slate-900 ring-1 ring-slate-500/30">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-surface-900 truncate">{tenant.name}</div>
                        <div className="text-[10px] text-surface-500 capitalize">{role}</div>
                      </div>
                    </button>
                  ))}
                {isSuperAdmin && (
                  <Link
                    to="/admin/tenants"
                    onClick={() => setSwitcherOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 border-t border-overlay/[0.06] text-xs text-surface-500 hover:text-surface-900 hover:bg-overlay/[0.04] transition-colors"
                  >
                    View all tenants
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {NAV.map(item => {
            // ── Group header (collapsible) ──
            if (isGroup(item)) {
              const Icon        = item.icon;
              const isOpen      = openGroups.has(item.label);
              const childActive = item.children.some(c => isActive(c.path));

              // Collapsed sidebar: render group as parent icon + flat
              // child icons beneath (matches SuperAdminLayout pattern).
              if (collapsed) {
                return (
                  <div key={item.label} className="space-y-0.5">
                    <div
                      onMouseEnter={(e) => showTooltip(item.label, e)}
                      onMouseLeave={hideTooltip}
                      className={cn(
                        'flex items-center justify-center px-0 py-2 rounded-xl text-sm',
                        childActive ? 'text-brand-600 dark:text-brand-400' : 'text-surface-400',
                      )}
                    >
                      <Icon size={16} />
                    </div>
                    {item.children.map(child => {
                      const ChildIcon = child.icon;
                      const active    = isActive(child.path);
                      return (
                        <div key={child.path}
                          onMouseEnter={(e) => showTooltip(child.label, e)}
                          onMouseLeave={hideTooltip}
                        >
                          <Link to={base + child.path} onClick={() => setMobileOpen(false)}
                            className={cn(
                              'flex items-center justify-center px-0 py-2 rounded-xl text-sm transition-all duration-200',
                              active
                                ? 'text-brand-600 dark:text-brand-400 bg-brand-500/8'
                                : 'text-surface-400 hover:text-surface-700 hover:bg-overlay/[0.04]',
                            )}>
                            <ChildIcon size={14} />
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                );
              }

              return (
                <div key={item.label} className="space-y-0.5">
                  <button
                    onClick={() => toggleGroup(item.label)}
                    className={cn(
                      'flex items-center gap-3 w-full px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200',
                      childActive
                        ? 'text-brand-600 dark:text-brand-400'
                        : 'text-surface-500 hover:text-surface-900 hover:bg-overlay/[0.04]',
                    )}
                  >
                    <Icon size={16} className={childActive ? 'text-brand-600 dark:text-brand-400' : ''} />
                    <span className="flex-1 text-left">{item.label}</span>
                    {isOpen
                      ? <ChevronDown  size={13} className="text-surface-400" />
                      : <ChevronRight size={13} className="text-surface-400" />}
                  </button>

                  {isOpen && (
                    <div className="space-y-0.5 ml-2 pl-3 border-l border-overlay/[0.08]">
                      {item.children.map(child => {
                        const ChildIcon = child.icon;
                        const active = isActive(child.path);
                        return (
                          <Link
                            key={child.path}
                            to={base + child.path}
                            onClick={() => setMobileOpen(false)}
                            className={cn(
                              'flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] font-medium transition-all duration-200',
                              active
                                ? 'text-brand-600 dark:text-brand-400 bg-brand-500/8 shadow-[inset_3px_0_0_0_#2AB3B1]'
                                : 'text-surface-500 hover:text-surface-900 hover:bg-overlay/[0.04]',
                            )}
                          >
                            <ChildIcon size={14} className={active ? 'text-brand-600 dark:text-brand-400' : ''} />
                            <span>{child.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // ── Leaf entry ──
            const Icon   = item.icon;
            const active = isActive(item.path);
            return (
              <div
                key={item.path}
                onMouseEnter={(e) => collapsed && showTooltip(item.label, e)}
                onMouseLeave={hideTooltip}
              >
                <Link to={base + item.path} onClick={() => setMobileOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200',
                    active
                      ? 'text-brand-600 dark:text-brand-400 bg-brand-500/8 shadow-[inset_3px_0_0_0_#2AB3B1]'
                      : 'text-surface-500 hover:text-surface-900 hover:bg-overlay/[0.04]',
                    collapsed && 'justify-center px-0',
                  )}>
                  <Icon size={16} className={active ? 'text-brand-600 dark:text-brand-400' : ''} />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              </div>
            );
          })}
        </nav>

        {/* Collapse toggle — sits above the user/profile row, mirrors
            the SuperAdminLayout placement. */}
        <div className="px-2 pt-1 border-t border-overlay/[0.06]">
          <button onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex w-full items-center justify-center gap-2 px-3 py-2 rounded-xl text-surface-500 hover:text-surface-700 hover:bg-surface-200/60 transition-all text-sm">
            <ChevronLeft size={14} className={cn('transition-transform', collapsed && 'rotate-180')} />
            {!collapsed && <span className="text-xs">Collapse</span>}
          </button>
        </div>

        {/* User profile + logout */}
        <div className="p-3 border-t border-overlay/[0.06]">
          {collapsed ? (
            <div className="flex flex-col items-center gap-1.5">
              <div
                onMouseEnter={(e) => showTooltip(user?.email || 'User', e)}
                onMouseLeave={hideTooltip}
              >
                <div className="w-7 h-7 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-500 text-[11px] font-bold">
                  {(user?.email || 'U').charAt(0).toUpperCase()}
                </div>
              </div>
              <div
                onMouseEnter={(e) => showTooltip(darkMode ? 'Switch to light mode' : 'Switch to dark mode', e)}
                onMouseLeave={hideTooltip}
              >
                <button onClick={toggleTheme}
                  className="p-1.5 rounded-lg hover:bg-surface-200 text-surface-500 hover:text-surface-700 transition-colors">
                  {darkMode ? <Sun size={13} /> : <Moon size={13} />}
                </button>
              </div>
              <div
                onMouseEnter={(e) => showTooltip('Sign out', e)}
                onMouseLeave={hideTooltip}
              >
                <button
                  onClick={async () => { await signOut(); navigate('/auth/login'); }}
                  className="p-1.5 rounded-lg hover:bg-surface-200 text-surface-500 hover:text-surface-700 transition-colors"
                >
                  <LogOut size={13} />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 px-2 py-2">
              <div className="w-7 h-7 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-500 text-[11px] font-bold shrink-0">
                {(user?.email || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-surface-900 truncate">{user?.email || 'User'}</div>
                <div className="text-[10px] text-surface-500">Tenant Admin</div>
              </div>
              <button onClick={toggleTheme} title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                className="p-1.5 rounded-lg hover:bg-surface-200 text-surface-500 hover:text-surface-700 transition-colors">
                {darkMode ? <Sun size={13} /> : <Moon size={13} />}
              </button>
              <button
                onClick={async () => { await signOut(); navigate('/auth/login'); }}
                title="Sign out"
                className="p-1.5 rounded-lg hover:bg-surface-200 text-surface-500 hover:text-surface-700 transition-colors"
              >
                <LogOut size={13} />
              </button>
            </div>
          )}
        </div>

        {/* Footer — hidden when collapsed to save space */}
        {!collapsed && (
          <div className="px-4 py-2.5 border-t border-overlay/[0.06] text-center">
            <p className="text-[10px] text-surface-400">Powered by Arrive Tech LLC. All rights reserved.</p>
          </div>
        )}
      </aside>

      {/* Floating tooltip for collapsed-sidebar icons */}
      {tooltip && (
        <div
          className="fixed left-[80px] z-[60] pointer-events-none -translate-y-1/2 px-2 py-1 rounded-md bg-surface-900 text-surface-0 text-xs shadow-lg whitespace-nowrap"
          style={{ top: tooltip.y }}
        >
          {tooltip.label}
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-w-0">
        {/* Slim top bar — holds the mobile sidebar trigger on small
            screens AND the "Signed in as" pill on all sizes. The
            sidebar already shows the user email + a generic "Tenant
            Admin" label at the bottom, but the pill in this top bar
            shows which SPECIFIC tenant they're managing right now
            (URL-aware) — critical for admins of multiple tenants.
            Stays sticky to the top of the scroll container. */}
        <header className="sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6 py-3 border-b border-overlay/[0.04] bg-surface-0/80 backdrop-blur-md">
          <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-surface-100">
            <Menu size={20} className="text-surface-600" />
          </button>
          {/* spacer keeps the pill right-aligned on desktop where the
              mobile menu button is hidden. */}
          <div className="hidden lg:block" />
          <SignedInPill />
        </header>
        <PlatformAnnouncementBanner />
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
